// src/pages/admin-orders/admin-orders.component.jsx
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useHistory } from 'react-router-dom';
import { selectOrders, selectOrdersLoading } from '../../redux/order/order.selectors';
import { fetchOrders, updateOrderStatus } from '../../redux/order/order.actions';
import './admin-orders.styles.scss';

const AdminOrders = ({ orders, loading, fetchOrders, updateOrderStatus }) => {
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
 

  // ✅ CHECK ADMIN LOGIN
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    const adminUsername = localStorage.getItem('adminUsername');
    
    if (!isLoggedIn || adminUsername !== 'admin') {
      // Redirect to admin login if not logged in
      window.location.href = '/admin-login';
      return;
    }
    
    fetchOrders();
  }, [fetchOrders]);

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.status === filter);

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'status-pending';
      case 'processing': return 'status-processing';
      case 'shipped': return 'status-shipped';
      case 'delivered': return 'status-delivered';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return '🟡 Pending';
      case 'processing': return '🔵 Processing';
      case 'shipped': return '🟣 Shipped';
      case 'delivered': return '🟢 Delivered';
      case 'cancelled': return '🔴 Cancelled';
      default: return status || 'Pending';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusUpdate = (orderId, newStatus) => {
    if (window.confirm(`Change order status to ${newStatus}?`)) {
      updateOrderStatus(orderId, newStatus);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('adminLoggedIn');
      localStorage.removeItem('adminUsername');
      localStorage.removeItem('adminLoginTime');
      window.location.href = '/admin-login';
    }
  };

  if (loading) {
    return (
      <div className="admin-orders-loading">
        <div className="loader"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="admin-orders-page">
      <div className="page-header">
        <div className="header-left">
          <h1>📦 Order Management</h1>
          <p>Manage all customer orders</p>
        </div>
        <div className="header-right">
          <button className="btn-logout" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="order-stats">
        <div className="stat-card">
          <span className="stat-number">{orders.length}</span>
          <span className="stat-label">Total Orders</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{orders.filter(o => o.status === 'pending').length}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{orders.filter(o => o.status === 'processing').length}</span>
          <span className="stat-label">Processing</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{orders.filter(o => o.status === 'delivered').length}</span>
          <span className="stat-label">Delivered</span>
        </div>
      </div>

      {/* Filters */}
      <div className="order-filters">
        <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          All ({orders.length})
        </button>
        <button className={`filter-btn ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
          Pending ({orders.filter(o => o.status === 'pending').length})
        </button>
        <button className={`filter-btn ${filter === 'processing' ? 'active' : ''}`} onClick={() => setFilter('processing')}>
          Processing ({orders.filter(o => o.status === 'processing').length})
        </button>
        <button className={`filter-btn ${filter === 'shipped' ? 'active' : ''}`} onClick={() => setFilter('shipped')}>
          Shipped ({orders.filter(o => o.status === 'shipped').length})
        </button>
        <button className={`filter-btn ${filter === 'delivered' ? 'active' : ''}`} onClick={() => setFilter('delivered')}>
          Delivered ({orders.filter(o => o.status === 'delivered').length})
        </button>
        <button className={`filter-btn ${filter === 'cancelled' ? 'active' : ''}`} onClick={() => setFilter('cancelled')}>
          Cancelled ({orders.filter(o => o.status === 'cancelled').length})
        </button>
      </div>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <p>No orders found</p>
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Items</th>
                <th>Total</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} onClick={() => setSelectedOrder(order)}>
                  <td><strong>{order.orderNumber || 'N/A'}</strong></td>
                  <td>{order.customerName}</td>
                  <td>{order.phone}</td>
                  <td>{order.itemCount || 0}</td>
                  <td>Rs. {order.totalAmount?.toLocaleString() || 0}</td>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <select 
                        value={order.status || 'pending'}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusUpdate(order.id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="order-modal" onClick={() => setSelectedOrder(null)}>
          <div className="order-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setSelectedOrder(null)}>×</button>
            <h2>Order Details</h2>
            <div className="order-details">
              <div className="detail-row">
                <strong>Order #:</strong> {selectedOrder.orderNumber || 'N/A'}
              </div>
              <div className="detail-row">
                <strong>Customer:</strong> {selectedOrder.customerName}
              </div>
              <div className="detail-row">
                <strong>Phone:</strong> {selectedOrder.phone}
              </div>
              <div className="detail-row">
                <strong>Email:</strong> {selectedOrder.email || 'N/A'}
              </div>
              <div className="detail-row">
                <strong>Address:</strong> {selectedOrder.address}
              </div>
              <div className="detail-row">
                <strong>City:</strong> {selectedOrder.city}
              </div>
              <div className="detail-row">
                <strong>Postal Code:</strong> {selectedOrder.postalCode || 'N/A'}
              </div>
              <div className="detail-row">
                <strong>Date:</strong> {formatDate(selectedOrder.createdAt)}
              </div>
              <div className="detail-row">
                <strong>Status:</strong> 
                <span className={`status-badge ${getStatusColor(selectedOrder.status)}`}>
                  {getStatusLabel(selectedOrder.status)}
                </span>
              </div>
              <div className="detail-row">
                <strong>Items:</strong>
                <ul className="order-items-list">
                  {selectedOrder.items?.map((item, index) => (
                    <li key={index}>
                      {item.name} x{item.quantity} = Rs. {(item.price * item.quantity).toLocaleString()}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="detail-row total-row">
                <strong>Total Amount:</strong> Rs. {selectedOrder.totalAmount?.toLocaleString() || 0}
              </div>
              {selectedOrder.notes && (
                <div className="detail-row">
                  <strong>Notes:</strong> {selectedOrder.notes}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  orders: selectOrders,
  loading: selectOrdersLoading
});

const mapDispatchToProps = (dispatch) => ({
  fetchOrders: () => dispatch(fetchOrders()),
  updateOrderStatus: (orderId, status) => dispatch(updateOrderStatus(orderId, status))
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminOrders);